import { type TurfItem } from './adapter';
import { type ReachCurveRow, type TurfReachResult } from './algorithm';
import { type ComparisonDimension } from './comparison';

export interface TurfItemReachRow {
  item: TurfItem;
  reach: TurfReachResult;
}

interface BuildTurfCsvOptions {
  basketState: string;
  comparisonDimensions?: ComparisonDimension[];
  completedRespondentCount: number;
  exportTimestamp: string;
  filteredRespondentCount: number;
  filterSummary: string;
  includeSpecialItems: boolean;
  itemReachRows: TurfItemReachRow[];
  maxSize: number;
  questionLabel: string;
  reachCurveRows: ReachCurveRow[];
}

type CsvCell = boolean | number | string;

export const buildTurfCsv = ({
  basketState,
  comparisonDimensions = [],
  completedRespondentCount,
  exportTimestamp,
  filteredRespondentCount,
  filterSummary,
  includeSpecialItems,
  itemReachRows,
  maxSize,
  questionLabel,
  reachCurveRows,
}: BuildTurfCsvOptions): string =>
  [
    toCsvRows([
      ['Section', 'Field', 'Value'],
      ['Header', 'Basket type', basketState],
      ['Header', 'Selected TURF question', questionLabel],
      ['Header', 'Filter summary', filterSummary],
      ['Header', 'Max size', maxSize],
      ['Header', 'Include special items', includeSpecialItems],
      ['Header', 'Export timestamp', exportTimestamp],
      ['Header', 'Completed respondent base', completedRespondentCount],
      ['Header', 'Filtered respondent base', filteredRespondentCount],
    ]),
    toCsvRows([
      ['Reach Curve'],
      ['Size', 'Item Added', 'Reach %', 'Reach Count', 'Frequency', 'Incremental Reach %'],
      ...reachCurveRows.map((row) => [
        row.size,
        row.itemAddedLabel,
        toPercent(row.pct),
        row.hit,
        row.frequency.toFixed(2),
        toPercent(row.incrementalPct),
      ]),
    ]),
    toCsvRows([
      ['Individual Item Reach'],
      ['Item', 'Reach %', 'Reach Count', 'Frequency', 'Special'],
      ...itemReachRows.map(({ item, reach }) => [
        item.label,
        toPercent(reach.pct),
        reach.hit,
        reach.frequency.toFixed(2),
        item.special,
      ]),
    ]),
    toCsvRows([
      ['Comparison'],
      ['Dimension', 'Group', 'Reach %', 'Reach Count', 'n', 'Index vs Overall %', 'Low n'],
      ...comparisonDimensions.flatMap((dimension) =>
        dimension.rows.map((row) => [
          dimension.label,
          row.label,
          toPercent(row.reach.pct),
          row.reach.hit,
          row.n,
          toSignedPercent(row.overIndexPct),
          row.lowN,
        ])
      ),
    ]),
  ].join('\n\n');

export const downloadCsv = ({ csv, filename }: { csv: string; filename: string }): void => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const sanitizeCsvFilename = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);

const toCsvRows = (rows: CsvCell[][]): string =>
  rows.map((row) => row.map((cell) => escapeCsvCell(String(cell))).join(',')).join('\n');

const escapeCsvCell = (cell: string): string => {
  if (!/[",\n]/.test(cell)) return cell;

  return `"${cell.replace(/"/g, '""')}"`;
};

const toPercent = (value: number): string => `${(value * 100).toFixed(1)}%`;

const toSignedPercent = (value: number): string =>
  `${value > 0 ? '+' : ''}${(value * 100).toFixed(1)}%`;
